// Service hours: the time a driver was off the road taking the car to a service
// the company instructed, claimed on Part B of the second shift form, checked
// against Uber and Cartrack, and decided by his Driver Servant. Clauses 6.10 to
// 6.12 of the v6 agreement. db/migrations/0600 to 0605.
//
// Every figure on this screen comes from Postgres: the offline hours on the
// shift, the Cartrack stop, the suggested figure and its working, paid hours,
// the rand difference. The page arranges them and sends a decision back.
// CLAUDE.md rule 4.
//
// WHO SEES WHAT. Every cockpit login can open the page, because a Driver
// Servant must see his drivers' claims to decide them. Who may decide a claim
// is decided per claim in the database (can_decide, can_referee), and rand
// figures arrive only for a login with finance. A button that would be refused
// is not drawn.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ChevronDown, ChevronRight, FileBarChart, RefreshCw, Timer, X } from 'lucide-react'
import {
  useDecideServiceHoursClaim,
  useDecideServiceHoursReferral,
  useGetServiceHours,
  useGetServiceHoursReport,
} from '../hooks/backend/payroll'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Textarea } from '../lib/shadcn/textarea'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import {
  formatDay,
  formatHours,
  formatHoursDelta,
  formatWhen,
  formatZar,
  groupClaims,
  mineOnly,
  statusTone,
  windowLabel,
  type Claim,
  type PageData,
  type ReportData,
} from '../utils/serviceHours'

type Tab = 'manager' | 'referee' | 'decided' | 'drivers'

export default function ServiceHours() {
  const pageQuery = useGetServiceHours<PageData>()
  const load = pageQuery.trigger
  const [params] = useSearchParams()
  const focus = params.get('claim')
  const [tab, setTab] = useState<Tab>('manager')
  const [expanded, setExpanded] = useState<string | null>(focus)
  const [notice, setNotice] = useState<string | null>(null)
  const [showReport, setShowReport] = useState(false)
  // Opens on the viewer's own drivers. A manager saw every manager's claims on
  // the first evening and could act on none of the others'.
  const [onlyMine, setOnlyMine] = useState(true)

  const refresh = useCallback(() => {
    void load({}, { skipCache: true }).catch(() => undefined)
  }, [load])
  useEffect(() => { refresh() }, [refresh])

  const data = pageQuery.data
  const hasMine = (data?.claims ?? []).some((c) => c.is_mine)
  const filterMine = onlyMine && hasMine
  const lists = useMemo(() => groupClaims(mineOnly(data?.claims ?? [], filterMine)), [data, filterMine])
  const referee = data?.referee_name || 'the referee'

  // A link from a task (?claim=) opens the list that claim is in.
  useEffect(() => {
    if (!focus || !data) return
    const c = data.claims.find((x) => x.id === focus)
    if (!c) return
    setTab(c.status === 'awaiting_manager' ? 'manager' : c.status === 'awaiting_musa' ? 'referee' : 'decided')
  }, [focus, data])

  if (pageQuery.loading && !data && !pageQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading service hours…</div>
  }
  if (pageQuery.error && !data) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{pageQuery.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!data) return null

  const current = tab === 'manager' ? lists.manager : tab === 'referee' ? lists.referee : lists.decided
  const tabs: { key: Tab; label: string; n: number | null }[] = [
    { key: 'manager', label: 'Waiting for a Driver Servant', n: lists.manager.length },
    { key: 'referee', label: `Referred to ${referee}`, n: lists.referee.length },
    { key: 'decided', label: 'Decided this pay period', n: lists.decided.length },
    { key: 'drivers', label: 'By driver', n: null },
  ]

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Timer className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Service hours</h1>
          <p className="text-xs text-muted-foreground">
            Time a driver was offline taking the car to a service the company instructed, added back to his online time before
            his hours are worked out, up to the time he was offline. Pay period {data.period.label}.
          </p>
        </div>
        {data.viewer.sees_money && (
          <Button variant="outline" size="sm" onClick={() => setShowReport((v) => !v)}>
            <FileBarChart className="mr-1 h-4 w-4" /> {showReport ? 'Hide' : 'Backfill'} report
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={refresh} disabled={pageQuery.loading}>
          {pageQuery.loading ? <Spinner className="mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />} Refresh
        </Button>
      </div>

      {!data.switches.addback_live && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Decided hours are recorded but not yet added to pay. They start counting when the add back is switched on, after the
            backfill report is approved. Until then every shift pays exactly as it does today.
          </span>
        </div>
      )}

      {notice && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-foreground">
          <span className="flex-1">{notice}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {hasMine && tab !== 'drivers' && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs">
          <span className="text-muted-foreground">Showing</span>
          <Button size="sm" variant={filterMine ? 'default' : 'outline'} className="h-7" onClick={() => setOnlyMine(true)}>My drivers</Button>
          <Button size="sm" variant={filterMine ? 'outline' : 'default'} className="h-7" onClick={() => setOnlyMine(false)}>Everyone</Button>
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto border-b border-border px-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'whitespace-nowrap border-b-2 px-3 py-2 text-sm',
              tab === t.key ? 'border-[hsl(var(--brand))] font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}{t.n !== null ? ` (${t.n})` : ''}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {showReport && data.viewer.sees_money && <ReportPanel />}

        {tab === 'drivers' ? (
          <DriverTable data={data} />
        ) : current.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {tab === 'manager' ? 'No claim is waiting for a Driver Servant.' : tab === 'referee' ? `Nothing is waiting for ${referee}.` : 'Nothing has been decided this pay period yet.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {current.map((c) => (
              <ClaimItem
                key={c.id}
                claim={c}
                referee={referee}
                addbackLive={data.switches.addback_live}
                open={expanded === c.id}
                onToggle={() => setExpanded((v) => (v === c.id ? null : c.id))}
                onDone={(msg) => { setNotice(msg); refresh() }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function Fact({ label, value, hint, tone }: { label: string; value: string; hint?: string | null; tone?: 'warn' }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('text-sm font-medium', tone === 'warn' ? 'text-amber-700 dark:text-amber-300' : 'text-foreground')}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}

function ClaimItem({
  claim: c, referee, addbackLive, open, onToggle, onDone,
}: { claim: Claim; referee: string; addbackLive: boolean; open: boolean; onToggle: () => void; onDone: (msg: string) => void }) {
  const ct = c.cartrack
  const yes30 = c.other?.driver_yes_answers_prior_30_days ?? null
  const booking = c.other?.booking ?? null
  const decided = !['awaiting_manager', 'awaiting_musa'].includes(c.status)

  return (
    <li className={cn('px-4 py-3', open && 'bg-muted/30')}>
      <button type="button" className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left" onClick={onToggle}>
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="font-medium text-foreground">{c.driver_name ?? 'Unknown driver'}</span>
        <span className="text-xs text-muted-foreground">{c.plate ?? 'no car'} · {formatDay(c.shift_date)} · {windowLabel(c.shift_window)} · {c.ref}</span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">claimed {c.parsed_ok ? formatHours(c.claimed_hours) : `"${c.answer_raw ?? ''}"`}</span>
          <span className="text-xs text-muted-foreground">suggested {formatHours(c.suggested_hours)}</span>
          {decided && c.validated_hours !== null && <span className="text-xs font-medium text-foreground">decided {formatHours(c.validated_hours)}</span>}
          {c.escalated && <Chip tone="red">Overdue, escalated</Chip>}
          {c.already_full_pay && <Chip tone="neutral">Already paid in full</Chip>}
          {c.recorded_in_time === false && <Chip tone="red">Recorded late</Chip>}
          <Chip tone={statusTone(c.status)}>{c.status_label}</Chip>
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 pl-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Fact label="Uber online" value={formatHours(c.online_hours)} hint={c.online_hours === null ? 'Uber has not reported this day yet' : null} />
            <Fact label="Offline on the shift" value={formatHours(c.offline_hours_on_shift)} hint="The most that can be added back" />
            <Fact label="Paid now" value={formatHours(c.paid_hours_now)} />
            <Fact
              label="Service yes answers, last 30 days"
              value={yes30 === null ? '—' : String(yes30)}
              tone={yes30 !== null && yes30 >= 5 ? 'warn' : undefined}
              hint={yes30 !== null && yes30 >= 5 ? 'Far more than a car is serviced. Check before accepting.' : null}
            />
          </div>

          <div className="rounded-md border border-border bg-background p-3 text-sm">
            <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Cartrack</div>
            {ct?.site ? (
              <p className="text-foreground">
                Stationary {formatHours(ct.stationary_hours)} over {ct.stops_at_site} stop{ct.stops_at_site === 1 ? '' : 's'} at <b>{ct.site}</b>,
                first arrival {ct.arrived}, last departure {ct.departed}, plus {formatHours(ct.travel_hours)} driving there and away.
                {typeof ct.other_cars_at_site === 'number' && ct.other_cars_at_site > 0 ? ` ${ct.other_cars_at_site} other Fleetly car${ct.other_cars_at_site === 1 ? '' : 's'} stopped there the same day.` : ''}
                {' '}Longest stop on the shift {formatHours(ct.longest_stop_hours)}; {ct.trips} trips.
              </p>
            ) : (
              <p className="text-muted-foreground">{ct?.summary ?? 'Not read yet. The evidence refreshes every hour.'}</p>
            )}
            <div className="mb-1 mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">Booking</div>
            <p className={booking ? 'text-foreground' : 'text-muted-foreground'}>
              {booking
                ? `${booking.ref}${booking.provider ? ` at ${booking.provider}` : ''}, ${booking.scheduled_at}. ${booking.instructed_to_this_driver ? 'Instructed to this driver in writing.' : booking.instructed_in_writing ? 'Instructed in writing to a different driver.' : 'Not instructed to anyone in writing.'}`
                : 'No service booking for this car on this shift.'}
            </p>
            <div className="mb-1 mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">Suggested figure</div>
            <p className="text-foreground">{c.suggestion_working ?? 'Not computed yet.'}</p>
            {!c.parsed_ok && c.parse_note && <p className="mt-1 text-amber-700 dark:text-amber-300">{c.parse_note}</p>}
          </div>

          <p className="text-xs text-muted-foreground">
            Submitted {formatWhen(c.submitted_at)}{c.record_deadline ? `, limit ${formatDay(c.record_deadline)}` : ''}.
            {c.manager_name ? ` Driver Servant: ${c.manager_name}.` : ' No Driver Servant is recorded for this driver.'}
            {c.due && !decided ? ` Due ${formatDay(c.due)}.` : ''}
            {c.task_ref ? <> <Link className="underline" to={`/tasks/${c.task_ref}`}>Task #{c.task_ref}</Link></> : null}
            {c.referred_by ? ` Referred by ${c.referred_by}${c.referral_note ? `: ${c.referral_note}` : '.'}` : ''}
            {c.evidence_at ? ` Evidence read ${formatWhen(c.evidence_at)}.` : ''}
          </p>

          {c.already_full_pay && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground">
              This shift already pays the full shift hours, so no figure decided here changes his pay. Decide it anyway, so his record
              and his page show the right answer.
            </p>
          )}

          {decided && !addbackLive && ['validated', 'amended', 'confirmed'].includes(c.status) && (
            <p className="text-xs text-muted-foreground">
              Recorded. Paid now does not move yet: decided hours count in pay once the add back is switched on.
            </p>
          )}

          {decided && (
            <p className="text-sm text-foreground">
              {c.status_label}{c.validated_hours !== null ? `: ${formatHours(c.validated_hours)}` : ''} by {c.decided_by ?? 'someone'} on {formatWhen(c.decided_at)}.
              {c.note ? ` ${c.note}` : ''}
            </p>
          )}

          {c.can_decide && <ManagerActions claim={c} referee={referee} onDone={onDone} />}
          {c.can_referee && <RefereeActions claim={c} onDone={onDone} />}
          {!decided && !c.can_decide && !c.can_referee && (
            <p className="text-xs text-muted-foreground">
              {c.status === 'awaiting_manager'
                ? `Only ${c.manager_name ?? "the driver's Driver Servant"} or an administrator can decide this claim.`
                : `Only ${referee} or an administrator can decide a referred claim.`}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

function ManagerActions({ claim: c, referee, onDone }: { claim: Claim; referee: string; onDone: (msg: string) => void }) {
  const decide = useDecideServiceHoursClaim<{ status: string; validated_hours?: number }>()
  const [mode, setMode] = useState<'none' | 'amend' | 'refer'>('none')
  const [hours, setHours] = useState(c.suggested_hours !== null ? String(c.suggested_hours) : '')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function send(action: 'validate' | 'amend' | 'refer') {
    setError(null)
    try {
      await decide.trigger({ id: c.id, action, hours: action === 'amend' ? hours : null, note: note || null })
      onDone(action === 'refer' ? `${c.ref} referred to ${referee}.` : `${c.ref} decided.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="brand" disabled={decide.loading || !!c.validate_blocked} onClick={() => void send('validate')}>
          Validate {c.parsed_ok ? formatHours(c.claimed_hours) : ''}
        </Button>
        <Button size="sm" variant={mode === 'amend' ? 'default' : 'outline'} onClick={() => setMode(mode === 'amend' ? 'none' : 'amend')}>Amend</Button>
        <Button size="sm" variant={mode === 'refer' ? 'default' : 'outline'} onClick={() => setMode(mode === 'refer' ? 'none' : 'refer')}>Refer to {referee}</Button>
      </div>
      {c.validate_blocked && <p className="text-xs text-amber-700 dark:text-amber-300">{c.validate_blocked}</p>}
      {mode === 'amend' && (
        <div className="space-y-2">
          <label className="block text-xs text-muted-foreground">
            Hours the car was off the road for the service, up to {formatHours(c.offline_hours_on_shift)}
            <Input className="mt-1 h-8 w-32" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
          </label>
          <Textarea rows={2} placeholder="Why this figure. The driver will see it." value={note} onChange={(e) => setNote(e.target.value)} />
          <Button size="sm" variant="brand" disabled={decide.loading} onClick={() => void send('amend')}>Save amended figure</Button>
        </div>
      )}
      {mode === 'refer' && (
        <div className="space-y-2">
          <Textarea rows={2} placeholder={`What ${referee} should look at (optional)`} value={note} onChange={(e) => setNote(e.target.value)} />
          <Button size="sm" variant="brand" disabled={decide.loading} onClick={() => void send('refer')}>Refer to {referee}</Button>
        </div>
      )}
      {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function RefereeActions({ claim: c, onDone }: { claim: Claim; onDone: (msg: string) => void }) {
  const decide = useDecideServiceHoursReferral<{ status: string }>()
  const [hours, setHours] = useState(c.claimed_hours !== null ? String(c.claimed_hours) : c.suggested_hours !== null ? String(c.suggested_hours) : '')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function send(action: 'confirm' | 'reject') {
    setError(null)
    try {
      await decide.trigger({ id: c.id, action, hours: action === 'confirm' ? hours : null, note: note || null })
      onDone(`${c.ref} ${action === 'confirm' ? 'confirmed' : 'rejected'}.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <label className="block text-xs text-muted-foreground">
        Hours to confirm, up to {formatHours(c.offline_hours_on_shift)}
        <Input className="mt-1 h-8 w-32" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
      </label>
      <Textarea rows={2} placeholder="The reason. Needed to reject, or to confirm a figure other than the claim. The driver will see it." value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="brand" disabled={decide.loading} onClick={() => void send('confirm')}>Confirm</Button>
        <Button size="sm" variant="outline" disabled={decide.loading} onClick={() => void send('reject')}>Reject</Button>
      </div>
      {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}

function DriverTable({ data }: { data: PageData }) {
  if (data.drivers.length === 0) {
    return <div className="px-4 py-10 text-center text-sm text-muted-foreground">No driver has a service hours claim this pay period.</div>
  }
  const money = data.viewer.sees_money
  return (
    <div className="overflow-x-auto p-4">
      <table className="w-full min-w-[46rem] text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-2 pr-3 font-medium">Driver</th>
            <th className="py-2 pr-3 font-medium">Claims</th>
            <th className="py-2 pr-3 font-medium">Claimed</th>
            <th className="py-2 pr-3 font-medium">Pending</th>
            <th className="py-2 pr-3 font-medium">Decided</th>
            <th className="py-2 pr-3 font-medium">Paid hours now</th>
            <th className="py-2 pr-3 font-medium">Difference decided hours make</th>
            <th className="py-2 pr-3 font-medium">Hours not worked</th>
            {money && <th className="py-2 pr-3 font-medium">Rand difference</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.drivers.map((d) => (
            <tr key={d.driver_id}>
              <td className="py-2 pr-3 font-medium text-foreground">{d.driver_name}</td>
              <td className="py-2 pr-3">{d.claims}{d.rejected ? `, ${d.rejected} not accepted` : ''}</td>
              <td className="py-2 pr-3">{formatHours(d.claimed_hours)}</td>
              <td className="py-2 pr-3">{d.pending_hours ? formatHours(d.pending_hours) : '—'}</td>
              <td className="py-2 pr-3">{formatHours(d.validated_hours)}</td>
              <td className="py-2 pr-3">{formatHours(d.paid_hours_now)}</td>
              <td className="py-2 pr-3">{formatHoursDelta(d.hours_difference)}</td>
              <td className="py-2 pr-3">{formatHours(d.unexplained_offline_hours)}</td>
              {money && <td className="py-2 pr-3">{formatZar(d.rand_difference, true)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-muted-foreground">
        Pending hours are shown as pending on the payslip preview and change nothing until decided. Hours not worked is offline
        time on the shift that no decided or pending reason covers; it stays unpaid.
        {data.switches.addback_live ? '' : ' Decided hours are not yet in pay: the difference column is what they will add once the add back is switched on.'}
      </p>
    </div>
  )
}

function ReportPanel() {
  const report = useGetServiceHoursReport<ReportData>()
  const load = report.trigger
  useEffect(() => { void load({ from: '2026-09-25' }, { skipCache: true }).catch(() => undefined) }, [load])
  const r = report.data

  return (
    <div className="border-b border-border bg-muted/20 p-4">
      <h2 className="text-sm font-semibold text-foreground">Backfill report, shifts from 25 September 2026</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        What each driver would receive in the pay period {r ? `${formatDay(r.period.start)} to ${formatDay(r.period.end)}` : ''} if every pending claim were decided at its suggested figure. No payroll figure changes until the add back is switched on.
      </p>
      {report.error && <p className="text-sm text-rose-700 dark:text-rose-300">{report.error}</p>}
      {report.loading && !r ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Working out pay under each figure…</p>
      ) : r ? (
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">Driver</th>
                  <th className="py-1 pr-3 font-medium">Shifts</th>
                  <th className="py-1 pr-3 font-medium">Claimed</th>
                  <th className="py-1 pr-3 font-medium">Suggested</th>
                  <th className="py-1 pr-3 font-medium">Gross now</th>
                  <th className="py-1 pr-3 font-medium">Difference once validated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {r.drivers.map((d) => (
                  <tr key={d.driver_id}>
                    <td className="py-1 pr-3 font-medium text-foreground">{d.driver_name}</td>
                    <td className="py-1 pr-3">{d.shifts}</td>
                    <td className="py-1 pr-3">{formatHours(d.claimed_hours)}</td>
                    <td className="py-1 pr-3">{formatHours(d.suggested_hours)}</td>
                    <td className="py-1 pr-3">{formatZar(d.gross_now)}</td>
                    <td className="py-1 pr-3">{formatZar(d.rand_difference_if_suggested, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.nmw_floor_effect.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Minimum wage floor, a separate switch</p>
              <p>
                Paying hours actually worked where a driver was online more than 11 hours would add:{' '}
                {r.nmw_floor_effect.map((n) => `${n.driver_name} ${formatZar(n.rand_difference, true)}`).join(', ')}.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
